/**
 * buildInfo — what build is THIS BUNDLE?
 *
 * Vite inlines ``import.meta.env.VITE_*`` at build time, so these values are
 * literals in the shipped JS. They describe the code the browser is actually
 * executing, with no network call, which makes them the one version fact that
 * still works when the API is the thing that is broken.
 *
 * Plumbing: ``buildspec-{dev,prod}.yml`` -> ``/tmp/frontend.env`` ->
 * ``docker-compose-build-*.yml`` build args -> ``frontend/Dockerfile.*``
 * ARG+ENV -> here. ``VITE_GIT_SHA`` already existed for the Sentry release
 * (``src/lib/sentry.ts``) on dev only; prod's bundle carried no sha at all
 * until the same cycle that added this file.
 *
 * WHY THIS IS NOT THE WHOLE ANSWER, and why ``BuildStamp`` also asks the API:
 * ``frontend-build`` and ``web`` are SEPARATE images, built by one build but
 * pulled by separate digests and recreated as separate containers. They can
 * therefore be running different code — on 2026-09-06 (deployment
 * d-L3RC058AK) exactly that happened and CodeDeploy still reported Succeeded.
 * This value alone would have reported the fresh sha and told us nothing. The
 * disagreement between this and ``/api/version/`` is the diagnostic.
 */

const readEnv = (key: string): string => {
  // Defensive read: ``import.meta.env`` is always present under Vite, but the
  // vitest/jsdom setup and any future SSR path are not guaranteed to have it,
  // and a version badge must never be the thing that throws on boot.
  const env = import.meta.env as Record<string, string | undefined> | undefined;
  return (env?.[key] ?? "").trim();
};

/** Mirrors the backend's ``UNKNOWN`` sentinel so both halves read the same. */
export const UNKNOWN = "unknown";

export interface FrontendBuild {
  /** Full 40-char commit sha, or ``UNKNOWN`` in a local ``vite dev`` run. */
  gitSha: string;
  /** ``dev`` | ``prod``, or ``UNKNOWN`` outside CodeBuild. */
  channel: string;
}

export const frontendBuild: FrontendBuild = {
  gitSha: readEnv("VITE_GIT_SHA") || UNKNOWN,
  channel: readEnv("VITE_BUILD_CHANNEL") || UNKNOWN,
};

/** First 7 chars of a sha — what a human actually reads and compares. */
export const shortSha = (sha: string | null | undefined): string => {
  const value = (sha ?? "").trim();
  if (!value || value === UNKNOWN) return UNKNOWN;
  return value.slice(0, 7);
};

/**
 * True only when both shas are KNOWN and different.
 *
 * Deliberately false when either side is unknown: "we cannot tell" is not
 * "they disagree", and a local dev session (no VITE_GIT_SHA, no stamp) must
 * not light up a mismatch warning every time.
 */
export const shasDisagree = (
  frontendSha: string | null | undefined,
  backendSha: string | null | undefined,
): boolean => {
  const fe = (frontendSha ?? "").trim();
  const be = (backendSha ?? "").trim();
  if (!fe || !be || fe === UNKNOWN || be === UNKNOWN) return false;
  return fe !== be;
};
